import "./Header.scss"

import {
  AccountMenu,
  ContentSwitcher,
  Filter,
  FooterVariant,
  LanguagePicker,
  NavigationItem,
  Search,
  SvgIcon,
  TabBar,
  toast,
} from "@skylead/component-library"
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js"
import classNames from "classnames"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { Dropdown } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroller"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import settings from "../../../constants/settings"
import * as appActions from "../../../redux/actions"
import { store } from "../../../redux/store"
import dataUtils from "../../../utils/data/data-utils"
import { useConfig, useGlobalTranslation, useTooltip } from "../../../utils/hook"
import { getTranslatedNotification } from "../../../utils/notification-utils"
import {
  hasPermission,
  PERMISSIONS,
  USER_TYPES,
} from "../../../utils/team-management/team-management-utils"
import {
  validateCardName,
  validateChangeInvoiceInfo,
  validateChangePassword,
} from "../../../utils/validation/validation-utils"
import Button, { ButtonVariant } from "../../atoms/Button"
import ChangeCompanyInfoModal from "../../atoms/ChangeCompanyInfoModal"
import LangText from "../../atoms/LangText"
import Modal, { ModalVariant } from "../../atoms/Modal"
import AccountSettings from "../../molecules/AccountSettings"
import InputGroup from "../InputGroup"
import InvoicesModal from "../InvoicesModal"
import ManualEmail from "../ManualEmail"

dataUtils.quillInitValues()

const Header = ({
  title,
  leftTitleIcon,
  rightTitleIcon,
  contentSwitcherItems,
  rightContentSwitcherItems,
  leftHeaderButton,
  rightHeaderButton,
  headerSearch,
  topHeaderTabBarItems,
  headerTabBarItems,
  filter,
  multiSelect,
  rightFilter,
  leftHeaderContent,
  statisticsFilters,
  showOnlyHeaderTitle,
  landingHeaderActions,
  campaignStatusBar,
}) => {
  const config = useConfig()
  const navigate = useNavigate()
  const { tt } = useGlobalTranslation()
  const elements = useElements()
  const stripe = useStripe()
  const [tooltip, setTooltip, closeTooltip] = useTooltip(10, 0)

  const [currentPage, setCurrentPage] = useState(0)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [showManualEmail, setShowManualEmail] = useState(false)
  const [showInvoicesModal, setShowInvoicesModal] = useState(false)
  const [countryOfIncorporationError, setCountryOfIncorporationError] = useState(null)

  const {
    accountID,
    userID,
    accountNotifications,
    formData,
    selectedTeam,
    teamAccounts,
    teamsWithPayment,
    savedCard,
    userData,
    availableSeats,
  } = useSelector(state => ({
    accountID: state.account.activeAccountID,
    userID: state.user.profile.id,
    accountNotifications: state.account.accountNotifications,
    formData: state.forms.formData,
    selectedTeam: state.teamManagement.selectedTeam,
    teamAccounts: state.teamManagement.teamAccounts.items,
    teamsWithPayment: state.payment.teamsWithPayment,
    savedCard: state.payment.savedCard,
    userData: state.user.profile,
    availableSeats: state.payment.availableSeats,
  }))

  const canViewBilling = userData.hasBilling
  const canViewInvoices = userData.hasInvoices

  const currentLanguage = localStorage.getItem("user-language") || userData.language || "en"
  const emailConfirmedClass = classNames("email-icon", {
    confirmed: userData.emailConfirmed,
  })

  const { loadingNotificationsList, selectedRows } = formData
  const currentTeamAccount = teamAccounts.find(account => account.id === Number(accountID)) || {}
  const hasActiveMailboxes = currentTeamAccount.activeMailboxes !== 0

  const dispatch = useDispatch()

  useEffect(() => {
    const loadInitialData = async () => {
      await dispatch(appActions.getTeamAccounts())
    }
    if (selectedTeam) {
      loadInitialData()
    }
  }, [selectedTeam])

  useEffect(() => {
    const loadData = async () => {
      const allPromises = [dispatch(appActions.getAccountStatuses(tt))]

      if (accountID) {
        allPromises.push(dispatch(appActions.getAccountNotifications(true)))

        if (
          hasPermission(PERMISSIONS.GLOBAL_LIMITS_VIEW) ||
          hasPermission(PERMISSIONS.ACCOUNT_HEALTH_VIEW)
        ) {
          allPromises.push(dispatch(appActions.getSettingsData()))
        }
      }

      if (selectedTeam?.canManagePayment === true) {
        if (accountID) {
          allPromises.push(dispatch(appActions.getAccountSpecificPrices(accountID)))
        }

        if (!availableSeats?.length) {
          allPromises.push(
            dispatch(appActions.getAllSubscriptions()),
            dispatch(appActions.getAvailableSeatsForUser()),
            dispatch(appActions.getAvailableSeatsForTeam()),
          )
        }
      }

      const proxies = store.getState().app.proxyCountries
      if (proxies && proxies.length > 0) {
        allPromises.push(
          dispatch(appActions.updateFormField("selectedProxy", proxies[0].countryCode)),
        )
      }

      await Promise.all(allPromises)
    }

    loadData()
  }, [accountID])

  useEffect(() => {
    const loadTeamsForInvoices = async () => {
      if (showInvoicesModal) {
        await dispatch(appActions.getTeamsWithPaymentPermissions())
      }
    }
    loadTeamsForInvoices()
  }, [showInvoicesModal])

  const loadMore = async () => {
    if (!loadingNotificationsList) {
      dispatch(appActions.updateFormField("loadingNotificationsList", true))

      await dispatch(
        appActions.getAccountNotifications(
          false,
          (currentPage + 1) * settings.notificationsLimit,
          settings.notificationsLimit,
        ),
      )
      setCurrentPage(currentPage + 1)
      dispatch(appActions.updateFormField("loadingNotificationsList", false))
    }
  }

  const seenInitialNotifications = async () => {
    closeTooltip()
    setSelectedNotification(null)

    const allNotificationsIds = accountNotifications?.notifications
      .filter(notf => !notf.seen)
      .map(notf => notf.id)
    if (allNotificationsIds.length > 0) {
      await dispatch(appActions.seenNotifications(allNotificationsIds))
    }
  }

  const showNotification = notification => {
    setSelectedNotification(notification)
  }

  const hasMore =
    accountNotifications?.count > (currentPage + 1) * settings.notificationsLimit &&
    !loadingNotificationsList

  const openHelpCenter = (e, href) => {
    if (href) {
      e.preventDefault()
      window.open(href, "_blank", "noopener,noreferrer")
    }
  }

  const createNewStripeToken = async name => {
    const card = elements.getElement(CardElement)
    const stripeResp = await stripe.createToken(card, {
      name,
    })
    if (stripeResp.token) {
      return stripeResp.token.id
    }
    const allErrors = { card: stripeResp.error.message }
    dispatch(appActions.setFormErrors(allErrors))
    return false
  }

  const onCloseChangeSubscriptionCardModal = () => {
    dispatch(appActions.closeModal())
  }

  const changePaymentCard = async () => {
    const cardName = store.getState().forms.formData?.cardName || savedCard.cardInfo?.name
    const allErrors = validateCardName({ cardName })
    if (allErrors) {
      dispatch(appActions.setFormErrors(allErrors))
      return false
    }

    const newStripeToken = await createNewStripeToken(cardName)
    if (newStripeToken) {
      const changeCardSuccess = dispatch(appActions.changeCard(newStripeToken))
      if (changeCardSuccess) {
        toast.success(tt("header.change-payment-card.toast.success"))
        onCloseChangeSubscriptionCardModal()
      } else {
        dispatch(
          appActions.setFormErrors({
            changeCardError: tt("header.change-payment-card.card-error"),
          }),
        )
        return false
      }
    } else {
      dispatch(
        appActions.setFormErrors({
          stripeTokenError: tt("header.change-payment-card.token-error"),
        }),
      )
      return false
    }
  }

  const setFieldInRedux = (field, value) => {
    dispatch(appActions.updateFormField(field, value))
  }

  const onShowChangeSubscriptionCardModal = () => {
    dispatch(
      appActions.showModal(
        ModalVariant.LARGE,
        tt("credit-card"),
        <>
          <div className="credit-card-modal-wrapper">
            <div className="credit-card-info">
              <p className="large">
                <LangText langKey="card-details" />
              </p>
              <p className="small credit-card-info-text">
                <LangText langKey="header.credit-card.msg.1" />
              </p>
              <p className="small-italic credit-card-info-stripe">
                <LangText langKey="header.credit-card.msg.2" />
              </p>
            </div>
            <div className="credit-card-inputs">
              <InputGroup field="card" label={tt("card-details")} type="card" />
              <InputGroup
                field="cardName"
                label={tt("name-on-a-card")}
                placeholder={tt("placeholder.card-name")}
              />
            </div>
          </div>
        </>,
        FooterVariant.TRANSACTIONAL,
        { label: tt("save"), onClick: changePaymentCard },
        { label: tt("cancel") },
        undefined,
        true,
      ),
    )
  }

  const selectCountryOfIncorporation = countryCode => {
    dispatch(appActions.updateFormField("countryOfIncorporation", countryCode))
    if (countryOfIncorporationError) {
      setCountryOfIncorporationError(null)
      dispatch(appActions.clearFormError("countryOfIncorporation"))
    }
  }

  const clearChangeCompanyInfoModalData = () => {
    setCountryOfIncorporationError(null)
    dispatch(
      appActions.updateFormFields({
        companyAddress: undefined,
        companyCity: undefined,
        companyState: undefined,
        companyPostalCode: undefined,
        countryOfIncorporation: undefined,
        companyName: undefined,
        taxId: undefined,
      }),
    )
    dispatch(appActions.clearFormErrors())
  }

  const onCloseChangeCompanyInfoModal = () => {
    clearChangeCompanyInfoModalData()
    dispatch(appActions.closeModal())
  }

  const saveInvoiceData = async () => {
    const { formData: formDataObject } = store.getState().forms

    const allErrors = validateChangeInvoiceInfo(formDataObject)
    dispatch(appActions.setFormErrors(allErrors))

    if (allErrors?.countryOfIncorporation) {
      setCountryOfIncorporationError(allErrors.countryOfIncorporation)
    } else if (countryOfIncorporationError) {
      dispatch(appActions.clearFormError("countryOfIncorporation"))
    }

    if (!allErrors) {
      const { id: customerId } = await dispatch(appActions.getPaymentCustomer())

      const invoiceInfo = {
        companyName: formDataObject.companyName,
        taxId: formDataObject.taxId || "",
        address: {
          streetAddress: formDataObject.companyAddress,
          city: formDataObject.companyCity,
          state: formDataObject.companyState || "",
          postalCode: formDataObject.companyPostalCode,
          country: formDataObject.countryOfIncorporation,
        },
      }

      const isSuccess = await dispatch(
        appActions.updateCompanyInvoiceInfo(tt, customerId, invoiceInfo),
      )

      if (isSuccess) {
        onCloseChangeCompanyInfoModal()
      }
    }
  }

  const changePassword = async event => {
    event.preventDefault()
    const { newPassword, oldPassword } = store.getState().forms.formData
    if (newPassword !== "" && oldPassword !== "") {
      dispatch(appActions.updateFormField("loadingChangePassword", true))

      dispatch(appActions.clearFormErrors())
      const allErrors = validateChangePassword({ newPassword, oldPassword })
      dispatch(appActions.setFormErrors(allErrors))

      if (!allErrors) {
        await dispatch(appActions.changePassword(tt, { newPassword, oldPassword }))
        dispatch(appActions.clearForm())
      }
      dispatch(appActions.updateFormField("loadingChangePassword", false))
    }
    dispatch(appActions.closeModal())
  }

  const onChangeCompanyInfoSave = async () => {
    await saveInvoiceData()
  }

  const onChangeCompanyInfoBack = () => {
    clearChangeCompanyInfoModalData()
    dispatch(appActions.closeModal())
  }

  const onShowAccountSettings = () => {
    dispatch(appActions.getOpenApiKey())
    dispatch(
      appActions.showModal(
        ModalVariant.LARGE,
        tt("account-settings"),
        <AccountSettings />,
        FooterVariant.TRANSACTIONAL,
        { label: tt("save"), onClick: changePassword },
        { label: tt("cancel"), onClick: () => dispatch(appActions.closeModal()) },
        undefined,
        true,
      ),
    )
  }

  const onShowChangeCompanyInfoModal = () => {
    dispatch(
      appActions.showModal(
        ModalVariant.LARGE,
        tt("change-company-info"),
        <ChangeCompanyInfoModal selectCountryOfIncorporation={selectCountryOfIncorporation} />,
        FooterVariant.TRANSACTIONAL,
        { label: tt("save"), onClick: onChangeCompanyInfoSave },
        { label: tt("cancel"), onClick: onChangeCompanyInfoBack },
        undefined,
        true,
      ),
    )
  }

  const logoutUser = () => {
    dispatch(appActions.logoutUser(navigate))
  }

  const goToMasterLogin = () => {
    navigate(`/users/${userID}`)
  }

  const onLanguageSelect = async lang => {
    await dispatch(appActions.changeLanguage(lang))

    // set in local storage
    localStorage.setItem("user-language", lang)

    window.location.reload()
  }

  return (
    <>
      <div className="header-container">
        {tooltip}
        <div className="header-title">
          <div className="header-title-wrapper">
            {leftTitleIcon && (
              <Button
                variant={ButtonVariant.GHOST}
                icon={leftTitleIcon.icon}
                onClick={leftTitleIcon.onClick}
                className="header-title-left-button"
              />
            )}
            <h1 className="truncate-text-1">{title}</h1>
            {rightTitleIcon && (
              <Button
                variant={ButtonVariant.GHOST}
                icon={rightTitleIcon.icon}
                onClick={rightTitleIcon.onClick}
                className="header-title-right-button"
              />
            )}
          </div>

          <div className="header-right-menu">
            {!landingHeaderActions && hasPermission(PERMISSIONS.CAMPAIGNS_EDIT) && (
              <div
                onMouseEnter={e => {
                  setTooltip({
                    event: e,
                    caretTop: true,
                    text: hasActiveMailboxes
                      ? tt("send-email")
                      : tt("header.campaign-edit.no-mailbox.tooltip"),
                    topPos: -50,
                    leftPos: hasActiveMailboxes ? 24 : 106,
                  })
                }}
                onMouseLeave={closeTooltip}
              >
                <NavigationItem
                  icon="mail-plus-28"
                  onClick={() => {
                    if (hasActiveMailboxes) setShowManualEmail(true)
                    closeTooltip()
                  }}
                  type="top-bar"
                />
              </div>
            )}
            {showManualEmail && hasPermission(PERMISSIONS.CAMPAIGNS_EDIT) && (
              <ManualEmail closeManualEmail={() => setShowManualEmail(false)} />
            )}
            {!landingHeaderActions && (
              <Dropdown className="notification-dropdown-wrapper">
                <Dropdown.Toggle className="campaign-item m-0 p-0">
                  <div
                    onMouseEnter={e =>
                      setTooltip({
                        event: e,
                        caretTop: true,
                        text: tt("notifications"),
                        topPos: -50,
                        leftPos: 28,
                      })
                    }
                    onMouseLeave={closeTooltip}
                  >
                    <NavigationItem
                      icon="bell-28"
                      type="top-bar"
                      onClick={seenInitialNotifications}
                    />
                  </div>
                  {accountNotifications.unseen > 0 && (
                    <div className="notification-badge">{accountNotifications.unseen}</div>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {!!selectedNotification && (
                    <span
                      onClick={() => {
                        setSelectedNotification(null)
                      }}
                      style={{ position: "absolute", top: 15, left: 10, cursor: "pointer" }}
                    >
                      <SvgIcon icon="arrow-left" style={{ width: 20, height: 20 }} />
                    </span>
                  )}
                  <h6 className="notification-dropdown-title">
                    <LangText langKey="notifications" />
                  </h6>
                  {selectedNotification ? (
                    <div style={{ padding: 10, display: "flex", flexDirection: "column" }}>
                      <p className="notification-message-long">
                        {getTranslatedNotification(selectedNotification.message)}
                      </p>{" "}
                      <span className="notification-time">
                        {moment
                          .duration(selectedNotification.created - Date.now())
                          .locale(currentLanguage)
                          .humanize(true)}
                      </span>
                    </div>
                  ) : (
                    <div className="notifications-list-wrapper">
                      <InfiniteScroll
                        useWindow={false}
                        pageStart={0}
                        loadMore={loadMore}
                        hasMore={hasMore}
                      >
                        {accountNotifications.notifications.length > 0 ? (
                          accountNotifications.notifications.map(notification => (
                            <span
                              key={notification.id}
                              className="logout-link dropdown-item"
                              onClick={() => {
                                showNotification(notification)
                              }}
                            >
                              <p className="notification-message">
                                {getTranslatedNotification(notification.message)}
                              </p>
                              <span className="notification-time">
                                {moment
                                  .duration(notification.created - Date.now())
                                  .locale(currentLanguage)
                                  .humanize(true)}
                              </span>
                            </span>
                          ))
                        ) : (
                          <p className="notification-message notification-message__empty text-center">
                            <LangText langKey="no-notifications-yet" />
                          </p>
                        )}
                      </InfiniteScroll>
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            )}
            {config.REACT_APP_HELP_CENTER_LINK && (
              <div
                onMouseEnter={e =>
                  setTooltip({
                    event: e,
                    caretTop: true,
                    text: tt("help-center"),
                    topPos: -50,
                    leftPos: 26,
                  })
                }
                onMouseLeave={closeTooltip}
              >
                <NavigationItem
                  icon="help-28"
                  type="top-bar"
                  onClick={e => openHelpCenter(e, config.REACT_APP_HELP_CENTER_LINK)}
                />
              </div>
            )}
            <div className="header-divider">
              <SvgIcon icon="header-divider" />
            </div>

            <LanguagePicker
              language={currentLanguage}
              tt={tt}
              onLanguageChange={onLanguageSelect}
            />

            <AccountMenu
              userData={userData}
              USER_TYPES={USER_TYPES}
              emailConfirmedClass={emailConfirmedClass}
              goToMasterLogin={goToMasterLogin}
              onShowAccountSettings={onShowAccountSettings}
              canViewBilling={canViewBilling}
              onShowChangeCompanyInfoModal={onShowChangeCompanyInfoModal}
              onShowChangeSubscriptionCardModal={onShowChangeSubscriptionCardModal}
              canViewInvoices={canViewInvoices}
              setShowInvoicesModal={setShowInvoicesModal}
              logoutUser={logoutUser}
              useConfig={useConfig}
              setFieldInRedux={setFieldInRedux}
              tt={tt}
            />
          </div>
        </div>

        {!showOnlyHeaderTitle && (
          <div className="control-bars">
            {topHeaderTabBarItems && (
              <div className="control-bar">
                <TabBar items={topHeaderTabBarItems} />
              </div>
            )}
            <div className="control-bar">
              <div className="subtitle">
                {multiSelect && selectedRows?.length > 0 ? (
                  <div className="w-100">{multiSelect}</div>
                ) : (
                  <>
                    <div className="tab-bar">
                      {contentSwitcherItems && (
                        <ContentSwitcher
                          items={contentSwitcherItems.items}
                          onClick={contentSwitcherItems.onClick}
                        />
                      )}
                      {campaignStatusBar && { ...campaignStatusBar }}
                      {headerTabBarItems && <TabBar items={headerTabBarItems} />}
                      {filter && <Filter {...filter} tt={tt} />}
                      {leftHeaderContent && { ...leftHeaderContent }}
                      {statisticsFilters && statisticsFilters()}
                    </div>
                    <div className="actions">
                      {rightContentSwitcherItems && (
                        <ContentSwitcher
                          items={rightContentSwitcherItems.items}
                          onClick={rightContentSwitcherItems.onClick}
                        />
                      )}
                      {headerSearch && (
                        <Search
                          placeholder={headerSearch.placeholder}
                          value={headerSearch.value}
                          onEnter={headerSearch.onEnter}
                          onChange={headerSearch.onChange}
                        />
                      )}

                      {rightFilter && <Filter {...rightFilter} tt={tt} />}

                      {leftHeaderButton && (
                        <div
                          onMouseEnter={leftHeaderButton.onMouseEnter}
                          onMouseLeave={leftHeaderButton.onMouseLeave}
                        >
                          <Button
                            variant={leftHeaderButton.variant}
                            icon={leftHeaderButton.icon}
                            onClick={leftHeaderButton.onClick}
                            hasMenuIcon={leftHeaderButton.hasMenuIcon}
                            disabled={leftHeaderButton.disabled}
                          >
                            {leftHeaderButton.label}
                          </Button>
                        </div>
                      )}

                      {rightHeaderButton && (
                        <div
                          onMouseEnter={rightHeaderButton.onMouseEnter}
                          onMouseLeave={rightHeaderButton.onMouseLeave}
                        >
                          <Button
                            variant={rightHeaderButton.variant}
                            icon={rightHeaderButton.icon}
                            onClick={rightHeaderButton.onClick}
                            hasMenuIcon={rightHeaderButton.hasMenuIcon}
                            disabled={rightHeaderButton.disabled}
                          >
                            {rightHeaderButton.label}
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {showInvoicesModal && (
          <Modal
            show={showInvoicesModal}
            type={ModalVariant.LARGE}
            title={tt("invoices")}
            content={<InvoicesModal teamsWithPayment={teamsWithPayment} />}
            footerVariant={FooterVariant.ACKNOWLEDGMENT}
            primaryBtn={{
              label: tt("close"),
              onClick: () => {
                setShowInvoicesModal(false)
                dispatch(appActions.closeModal())
              },
            }}
          />
        )}
      </div>
    </>
  )
}

// Header.propTypes = {}

export default React.memo(Header)
